import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/store/index';
import { StoreKey } from '@/configs/storeKeys';
import {
	AdjustableHeights,
	CreatorAccessory,
	CreatorEndings,
	CreatorOptions,
	CreatorPost,
	CreatorRendererOptions,
	CreatorState,
	CreatorStep,
} from '@/types/creator';
import { ThreeEvent, ThreeEvents, UserDataObjectType } from '@/renderer/types';
import { ProductTypeVariation } from '@/api/ProductTypeVariationApi';
import { Product } from '@/api/ProductApi';
import {
	deserializeCreatorFromSerialized,
	detectMaxSpacingExceed,
	getAvailableAccessories,
	getAvailableAttributeValues,
	getInitialSelectedValues,
	setSteps,
} from '@/utils/creatorUtils';
import { AttributeValue } from '@/api/AttributeValueApi';
import { AccessoryType, LedType } from '@/api/AccessoryApi';
import { Door } from '@/api/DoorApi';
import { CreatorActionType, UUID } from '@/types/types';
import RENDERER_CONFIG from '@/configs/rendererConfig';
import ProductTypeApi from '@/api/ProductTypeApi';

const initialOptions: CreatorOptions = {
	collisionDetected: false,
	isMobileAsideOpened: false,
	accessoriesDrawerType: '',
	currentStep: CreatorStep.DIMENSIONS,
	withExtraPosts: false,
	autoMiddlePostPositioning: true,
	endingsConfiguration: {
		upperEnding: false,
		lowerEnding: false,
	},
	actionsOnAccessoryDelete: {},
	pendingRendererActions: [],
	availableSteps: [
		CreatorStep.DIMENSIONS,
		CreatorStep.ACCESSORIES,
		CreatorStep.COLORS,
		CreatorStep.SUMMARY,
	],
	enabledSteps: [CreatorStep.DIMENSIONS],
	accessoriesAccordionOpened: null,
	measurementVisible: false,
};

const initialState: CreatorState = {
	configurationName: '',
	product: null,
	productType: null,
	productTypeVariations: [],
	productTypeVariation: null,
	allAttributes: [],
	allAccessories: [],
	renderer: {
		loading: false,
		distance: RENDERER_CONFIG.INITIAL_DISTANCE,
	},
	creatorPosts: [],
	adjustableHeights: {
		handle: 0,
		upperEnding: 0,
		lowerEnding: 0,
		total: 0,
	},
	availableAttributeValues: {
		MAIN: [],
		POSTS: [],
		ENDINGS: [],
		STEP_ACCESSORIES: [],
	},
	selectedValues: {},
	customValues: {},
	availableAccessories: {
		[AccessoryType.ACCESS_CONTROL]: [],
		[AccessoryType.DRIVER]: [],
		[AccessoryType.LED]: [],
		[AccessoryType.CABLE_GLAND]: [],
	},
	creatorAccessories: [],
	creatorEndings: {
		[UserDataObjectType.UPPER_ENDING_ADJUSTABLE]: {
			selectedValues: {},
			customValues: {},
		},
		[UserDataObjectType.LOWER_ENDING_ADJUSTABLE]: {
			selectedValues: {},
			customValues: {},
		},
	},
	selectedDoor: null,
	options: initialOptions,
};

export const creatorSlice = createSlice({
	name: StoreKey.CREATOR,
	initialState,
	reducers: {
		initialize: (
			state,
			{
				payload,
			}: PayloadAction<
				Awaited<ReturnType<typeof ProductTypeApi.getInitialData>>
			>
		) => {
			state.renderer.loading = false;

			state.allAttributes = payload.allAttributes;
			state.allAccessories = payload.allAccessories;
			state.productType = payload.productType;

			state.productTypeVariations = payload.productTypeVariations.toSorted(
				(a, b) => a.listOrder - b.listOrder
			);
		},
		loadProduct: (
			state,
			{
				payload,
			}: PayloadAction<{
				initialData: Awaited<ReturnType<typeof ProductTypeApi.getInitialData>>;
				product: Product;
			}>
		) => {
			state.renderer.loading = false;

			const { initialData, product } = payload;
			const { productType, productTypeVariations } = initialData;

			const { serializedCreator } = product;
			state.allAttributes = initialData.allAttributes;
			state.allAccessories = initialData.allAccessories;

			state.configurationName = product.name;

			// if no prod.id => configuration loaded from local
			if (product.id) state.product = product;

			state.productType = productType;
			state.productTypeVariations = productTypeVariations.toSorted(
				(a, b) => a.listOrder - b.listOrder
			);

			state.productTypeVariation = product.productTypeVariation!;
			state.availableAccessories = getAvailableAccessories(
				state.productTypeVariation
			);

			state.availableAttributeValues = getAvailableAttributeValues(
				state.productTypeVariation!,
				state.allAttributes
			);

			const deserializedCreator = deserializeCreatorFromSerialized(
				serializedCreator,
				state.productTypeVariation!,
				state.allAttributes
			);

			state.selectedValues = deserializedCreator.selectedValues;
			state.customValues = deserializedCreator.customValues;
			state.adjustableHeights = deserializedCreator.adjustableHeights;
			state.creatorPosts = deserializedCreator.creatorPosts;
			state.creatorAccessories = deserializedCreator.creatorAccessories;
			state.creatorEndings = deserializedCreator.creatorEndings;

			state.options.withExtraPosts = serializedCreator.options.withExtraPosts;
			state.options.endingsConfiguration =
				serializedCreator.options.endingsConfiguration;
			state.options.actionsOnAccessoryDelete =
				serializedCreator.options.actionsOnAccessoryDelete;

			setSteps(state);
		},
		selectVariation: (
			state,
			{ payload }: PayloadAction<ProductTypeVariation>
		) => {
			state.creatorPosts = [];
			state.creatorAccessories = [];
			state.creatorEndings = {
				[UserDataObjectType.UPPER_ENDING_ADJUSTABLE]: {
					selectedValues: {},
					customValues: {},
				},
				[UserDataObjectType.LOWER_ENDING_ADJUSTABLE]: {
					selectedValues: {},
					customValues: {},
				},
			};

			state.adjustableHeights = {
				handle: payload.lengthSettings.handle.min,
				upperEnding: 0,
				lowerEnding: 0,
				total: 0,
			};

			state.options = {
				...initialOptions,
				availableSteps: [
					CreatorStep.DIMENSIONS,
					CreatorStep.ACCESSORIES,
					CreatorStep.COLORS,
					CreatorStep.SUMMARY,
				],
			};

			state.selectedDoor = null;

			state.productTypeVariation = payload;

			state.availableAttributeValues = getAvailableAttributeValues(
				state.productTypeVariation,
				state.allAttributes
			);
			state.selectedValues = getInitialSelectedValues(
				state.productTypeVariation,
				state.allAttributes,
				payload.defaultAttributeValues
			);

			state.availableAccessories = getAvailableAccessories(
				state.productTypeVariation
			);

			setSteps(state);
		},
		updateHeights: (
			state,
			{ payload }: PayloadAction<Partial<AdjustableHeights>>
		) => {
			Object.assign(state.adjustableHeights, payload);
		},
		setTotalHeight: (state, { payload }: PayloadAction<number>) => {
			state.adjustableHeights.total = payload;
		},
		setWithExtraPosts: (state, { payload }: PayloadAction<boolean>) => {
			state.options.withExtraPosts = payload;
		},
		addPost: (state, { payload }: PayloadAction<CreatorPost>) => {
			state.creatorPosts.push(payload);
			setSteps(state);
		},
		removePost: (state, { payload }: PayloadAction<CreatorPost>) => {
			state.creatorPosts = state.creatorPosts.filter(
				(post) => post.id !== payload.id
			);

			detectMaxSpacingExceed(
				state.creatorPosts,
				state.productTypeVariation?.middlePostMaxSpacing || Infinity
			);
			setSteps(state);
		},
		setPostPosition: (
			state,
			{ payload }: PayloadAction<{ id: UUID; posY: number }>
		) => {
			for (const post of state.creatorPosts) {
				if (payload.id !== post.id) continue;

				post.posY = payload.posY;
			}

			detectMaxSpacingExceed(
				state.creatorPosts,
				state.productTypeVariation?.middlePostMaxSpacing || Infinity
			);
		},
		setPostLinkedAccessory: (
			state,
			{
				payload,
			}: PayloadAction<{
				postId: UUID;
				linkedCreatorAccessoryId: UUID | null;
			}>
		) => {
			for (const post of state.creatorPosts) {
				if (payload.postId !== post.id) continue;
				post.linkedCreatorAccessoryId = payload.linkedCreatorAccessoryId;
				break;
			}
		},
		selectProductAttributeValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				attributeId: UUID;
				attributeValue: AttributeValue | null;
			}>
		) => {
			state.selectedValues[payload.attributeId] = payload.attributeValue;
			setSteps(state);
		},
		changeCustomValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				attributeId: UUID;
				value: string;
			}>
		) => {
			state.customValues[payload.attributeId] = payload.value;
		},
		selectPostAttributeValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				postId: UUID;
				attributeId: UUID;
				attributeValue: AttributeValue;
			}>
		) => {
			for (const post of state.creatorPosts) {
				if (post.id !== payload.postId) continue;
				post.selectedValues[payload.attributeId] = payload.attributeValue;
				break;
			}
			setSteps(state);
		},
		changePostCustomValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				postId: UUID;
				attributeId: UUID;
				value: string;
			}>
		) => {
			for (const post of state.creatorPosts) {
				if (post.id !== payload.postId) continue;
				post.customValues[payload.attributeId] = payload.value;
				break;
			}
			setSteps(state);
		},
		setEndingConfiguration: (
			state,
			{
				payload,
			}: PayloadAction<{
				type: 'upperEnding' | 'lowerEnding';
				value: boolean;
			}>
		) => {
			state.options.endingsConfiguration[payload.type] = payload.value;
			setSteps(state);
		},
		selectEndingAttributeValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				type: keyof CreatorEndings;
				attributeId: UUID;
				attributeValue: AttributeValue;
			}>
		) => {
			state.creatorEndings[payload.type].selectedValues[payload.attributeId] =
				payload.attributeValue;
			setSteps(state);
		},
		changeEndingCustomValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				type: keyof CreatorEndings;
				attributeId: UUID;
				value: string;
			}>
		) => {
			state.creatorEndings[payload.type].customValues[payload.attributeId] =
				payload.value;
			setSteps(state);
		},
		updateCreator: (
			state,
			{ payload }: PayloadAction<Partial<CreatorState>>
		) => {
			Object.assign(state, payload);
		},
		updateOptions: (
			state,
			{ payload }: PayloadAction<Partial<CreatorOptions>>
		) => {
			Object.assign(state.options, payload);
		},
		setAutoMiddlePostPositioning: (
			state,
			{ payload }: PayloadAction<boolean>
		) => {
			state.options.autoMiddlePostPositioning = payload;
		},
		updateRenderer: (
			state,
			{ payload }: PayloadAction<Partial<CreatorRendererOptions>>
		) => {
			Object.assign(state.renderer, payload);
		},
		handleRendererEvent: (
			state,
			{
				payload,
			}: PayloadAction<{
				type: ThreeEvent;
				data: ThreeEvents[ThreeEvent];
			}>
		) => {
			const { type } = payload;

			switch (type) {
				case ThreeEvent.DISTANCE:
					state.renderer.distance =
						payload.data as ThreeEvents[ThreeEvent.DISTANCE];
					break;
				case ThreeEvent.SET_ADJUSTABLE_HEIGHTS:
					Object.assign(state.adjustableHeights, payload.data);
					break;
				case ThreeEvent.DETECTED_COLLISION:
					const collisionDetected =
						payload.data as ThreeEvents[ThreeEvent.DETECTED_COLLISION];
					state.options.collisionDetected = collisionDetected;
					break;
				case ThreeEvent.UPDATE_OBJECT_POS_Y: {
					const data =
						payload.data as ThreeEvents[ThreeEvent.UPDATE_OBJECT_POS_Y];

					for (const creatorPost of state.creatorPosts) {
						if (creatorPost.id !== data.id) continue;

						creatorPost.posY = data.posY;
						break;
					}

					for (const creatorAccessory of state.creatorAccessories) {
						if (creatorAccessory.refId !== data.id) continue;
						creatorAccessory.posY = data.posY;
						break;
					}

					break;
				}
				case ThreeEvent.UPDATE_OBJECTS_POS_Y:
					const data =
						payload.data as ThreeEvents[ThreeEvent.UPDATE_OBJECTS_POS_Y];
					for (const item of data) {
						for (const creatorPost of state.creatorPosts) {
							if (creatorPost.id !== item.id) continue;
							creatorPost.posY = item.posY;
							continue;
						}
						for (const creatorAccessory of state.creatorAccessories) {
							if (creatorAccessory.refId !== item.id) continue;
							creatorAccessory.posY = item.posY;
							continue;
						}
					}
			}
		},
		addCreatorAccessory: (
			state,
			{ payload }: PayloadAction<CreatorAccessory>
		) => {
			state.creatorAccessories.push(payload);
			state.options.accessoriesDrawerType = '';

			if (!payload.parentCreatorAccessoryRefId)
				state.options.accessoriesAccordionOpened = payload.refId;
		},
		updateCreatorAccessoryCustomField: (
			state,
			{
				payload,
			}: PayloadAction<{
				refIds: UUID[];
				key: string;
				value: string;
			}>
		) => {
			for (const accessory of state.creatorAccessories) {
				if (!payload.refIds.includes(accessory.refId)) continue;
				accessory.customFieldAnswers[payload.key] = payload.value;
				break;
			}
		},
		updateCreatorAccessoryData: (
			state,
			{
				payload,
			}: PayloadAction<{
				refId: UUID;
				data: Partial<CreatorAccessory>;
				ledType?: LedType;
			}>
		) => {
			for (const accessory of state.creatorAccessories) {
				if (accessory.refId !== payload.refId) continue;
				Object.assign(accessory, payload.data);
				break;
			}
		},
		requestRemoveAccessory: (
			state,
			{ payload }: PayloadAction<UUID | null>
		) => {},
		removeCreatorAccessories: (state, { payload }: PayloadAction<UUID[]>) => {
			const toRemoveSet = new Set(payload);

			for (const refId of payload) {
				const acc = state.creatorAccessories.find((a) => a.refId === refId);
				if (!acc) continue;

				for (const attr of acc.availableAttributes) {
					const actions =
						state.options.actionsOnAccessoryDelete?.[refId]?.[attr.id];
					if (actions) {
						state.options.pendingRendererActions.push(...actions);
					}
				}
				delete state.options.actionsOnAccessoryDelete[refId];
			}

			const hasSpecialLed = state.creatorAccessories.some(
				(acc) =>
					toRemoveSet.has(acc.refId) &&
					[LedType.LED_TAPE, LedType.LED_POINT].includes(
						acc.variationAccessory.accessory.ledType
					)
			);

			if (hasSpecialLed)
				state.creatorAccessories = state.creatorAccessories.filter((acc) =>
					[LedType.NONE, LedType.LED_PLUG].includes(
						acc.variationAccessory.accessory.ledType
					)
				);

			state.creatorAccessories = state.creatorAccessories.filter(
				(acc) => !toRemoveSet.has(acc.refId)
			);
		},
		removeLedPoint: (state, { payload }: PayloadAction<UUID>) => {
			state.creatorAccessories = state.creatorAccessories.filter(
				(accessory) => accessory.refId !== payload
			);
		},
		toggleLedPointsAutoPositioning: (
			state,
			{ payload }: PayloadAction<boolean>
		) => {
			for (const creatorAccessory of state.creatorAccessories) {
				if (
					creatorAccessory.variationAccessory.accessory.ledType !==
					LedType.LED_POINT
				)
					continue;
				creatorAccessory.options.autoPositioning = payload;
			}
		},
		selectAccessoryAttributeValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				refIds: UUID[];
				attributeId: UUID;
				attributeValue: AttributeValue;
			}>
		) => {
			for (const creatorAccessory of state.creatorAccessories) {
				if (!payload.refIds.includes(creatorAccessory.refId)) continue;
				creatorAccessory.selectedValues[payload.attributeId] =
					payload.attributeValue;
				break;
			}

			const toggleVisiblityActions = payload.attributeValue.actions.filter(
				(action) => action.type === CreatorActionType.TOGGLE_VISIBILITY
			);

			if (
				!toggleVisiblityActions.length ||
				state.options.actionsOnAccessoryDelete[payload.refIds[0]]?.[
					payload.attributeId
				]
			)
				return;

			if (!state.options.actionsOnAccessoryDelete[payload.refIds[0]])
				state.options.actionsOnAccessoryDelete[payload.refIds[0]] = {};

			if (
				!state.options.actionsOnAccessoryDelete[payload.refIds[0]][
					payload.attributeId
				]
			)
				state.options.actionsOnAccessoryDelete[payload.refIds[0]][
					payload.attributeId
				] = [];

			state.options.actionsOnAccessoryDelete[payload.refIds[0]][
				payload.attributeId
			] = toggleVisiblityActions.map((action) => {
				const hideTargets = action.payload.hideTargets?.split(',') || [];
				const newTarget = hideTargets[0];

				const newHideTargets = hideTargets.slice(1);
				newHideTargets.push(action.payload.target!);

				return {
					id: action.id,
					attributeValueId: action.attributeValueId,
					type: CreatorActionType.TOGGLE_VISIBILITY,
					payload: {
						asset: action.payload.asset,
						target: newTarget,
						hideTargets: newHideTargets.join(','),
					},
				};
			});
		},
		changeAccessoryCustomValue: (
			state,
			{
				payload,
			}: PayloadAction<{
				accessoryRefId: UUID;
				attributeId: UUID;
				value: string;
			}>
		) => {
			for (const creatorAccessory of state.creatorAccessories) {
				if (creatorAccessory.refId !== payload.accessoryRefId) continue;

				const attribute = creatorAccessory.availableAttributes.find(
					(attr) => attr.id === payload.attributeId
				);
				if (!attribute) continue;

				creatorAccessory.customValues[attribute.id] = payload.value;
				break;
			}
		},
		setSelectedDoor: (state, { payload }: PayloadAction<Door | null>) => {
			state.selectedDoor = payload;
		},
		setCreatorStep: (state, { payload }: PayloadAction<CreatorStep>) => {
			state.options.currentStep = payload;
		},
		setStepsAvailability: (state) => {
			setSteps(state);
		},
		toggleMeasurementVisible: (state) => {
			state.options.measurementVisible = !state.options.measurementVisible;
		},
		reset: (state) => initialState,
	},
});

export const creatorActions = creatorSlice.actions;

export const selectCreatorState = (state: RootState) => state.creator;
export default creatorSlice.reducer;
